/**
 * Calendar Booking Component
 *
 * Handles the booking flow for demo appointments using Google Calendar integration.
 * Can be used as a modal or standalone component.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Calendar, Clock, ExternalLink, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { calendarService } from '@/services';
import type { CalendarBookingResponse } from '@/services/shared/calendarService';

export interface CalendarBookingProps {
  isOpen: boolean;
  onClose: () => void;
  userEmail: string;
  userName: string;
  onBookingComplete?: (bookingData: CalendarBookingResponse) => void;
  onBookingError?: (error: string) => void;
}

export interface BookingState {
  isLoading: boolean;
  bookingUrl: string | null;
  error: string | null;
  success: boolean;
}

export const CalendarBooking: React.FC<CalendarBookingProps> = ({
  isOpen,
  onClose,
  userEmail,
  userName,
  onBookingComplete,
  onBookingError,
}) => {
  const [bookingState, setBookingState] = useState<BookingState>({
    isLoading: false,
    bookingUrl: null,
    error: null,
    success: false,
  });

  const initiateBooking = useCallback(async () => {
    setBookingState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      // First, validate calendar service configuration
      const isConfigured = await calendarService.validateConfiguration();

      if (!isConfigured) {
        // Fallback to manual Google Calendar link
        const manualLink = calendarService.generateManualBookingLink(
          'Community App Demo Session',
          `Demo session for ${userName} (${userEmail}). Please join us to explore how our community platform can benefit your organization.`,
          userEmail,
          30,
        );

        setBookingState({
          isLoading: false,
          bookingUrl: manualLink,
          error: null,
          success: true,
        });
        return;
      }

      // Try to create an automated booking page
      const response = await calendarService.createDemoBookingPage(userEmail, userName, {
        title: 'Community App Demo Session',
        description:
          'Personalized demo to explore our community platform features and discuss how they can benefit your organization.',
        duration: 30,
        bufferTime: 15,
      });

      if (response.success && response.bookingUrl) {
        setBookingState({
          isLoading: false,
          bookingUrl: response.bookingUrl,
          error: null,
          success: true,
        });
        onBookingComplete?.(response);
      } else {
        throw new Error(response.error || 'Failed to create booking page');
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to create calendar booking';

      // Fallback to manual Google Calendar link on error
      const manualLink = calendarService.generateManualBookingLink(
        'Community App Demo Session',
        `Demo session for ${userName} (${userEmail}). Please join us to explore how our community platform can benefit your organization.`,
        userEmail,
        30,
      );

      setBookingState({
        isLoading: false,
        bookingUrl: manualLink,
        error: null,
        success: true,
      });

      onBookingError?.(errorMessage);
    }
  }, [userEmail, userName, onBookingComplete, onBookingError]);

  // Initialize booking when modal opens
  useEffect(() => {
    if (isOpen && !bookingState.bookingUrl && !bookingState.error) {
      initiateBooking();
    }
  }, [isOpen, bookingState.bookingUrl, bookingState.error, initiateBooking]);

  const handleOpenCalendar = () => {
    if (bookingState.bookingUrl) {
      window.open(bookingState.bookingUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleClose = () => {
    setBookingState({
      isLoading: false,
      bookingUrl: null,
      error: null,
      success: false,
    });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-blue-600" />
            Schedule Your Demo
          </DialogTitle>
          <DialogDescription>
            Let's schedule a personalized demo to show you how our community platform can benefit
            your organization.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {bookingState.isLoading ? (
            <div className="flex flex-col items-center justify-center py-8 space-y-4">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              <p className="text-sm text-muted-foreground">Setting up your booking page...</p>
            </div>
          ) : bookingState.error ? (
            <div className="flex flex-col items-center space-y-4 py-4">
              <AlertCircle className="h-12 w-12 text-red-500" />
              <div className="text-center">
                <h3 className="text-lg font-semibold text-red-600">Booking Error</h3>
                <p className="text-sm text-muted-foreground mt-1">{bookingState.error}</p>
              </div>
              <Button onClick={initiateBooking} variant="outline" className="mt-4">
                Try Again
              </Button>
            </div>
          ) : bookingState.success && bookingState.bookingUrl ? (
            <div className="space-y-4">
              <div className="flex items-center space-x-3 p-4 bg-green-50 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-600 shrink-0" />
                <div>
                  <h3 className="text-sm font-semibold text-green-800">Booking Page Ready!</h3>
                  <p className="text-sm text-green-700">
                    Your demo booking page has been prepared.
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Clock className="h-4 w-4" />
                  45-minute demo session
                </div>
                <div className="text-sm text-muted-foreground">
                  <strong>What to expect:</strong>
                  <ul className="mt-2 space-y-1 ml-4 list-disc">
                    <li>Personalized platform walkthrough</li>
                    <li>Discussion of your specific needs</li>
                    <li>Q&A session with our team</li>
                    <li>Next steps planning</li>
                  </ul>
                </div>
              </div>

              <div className="flex space-x-3">
                <Button onClick={handleOpenCalendar} className="flex-1">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Open Booking Page
                </Button>
                <Button onClick={handleClose} variant="outline">
                  Done
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-8">
              <p className="text-sm text-muted-foreground">Preparing your booking options...</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Standalone booking button component for direct integration
 */
export interface BookingButtonProps {
  userEmail: string;
  userName: string;
  onBookingComplete?: (bookingData: CalendarBookingResponse) => void;
  onBookingError?: (error: string) => void;
  className?: string;
  children?: React.ReactNode;
}

export const BookingButton: React.FC<BookingButtonProps> = ({
  userEmail,
  userName,
  onBookingComplete,
  onBookingError,
  className,
  children,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <>
      <Button onClick={() => setIsModalOpen(true)} className={className}>
        {children || (
          <>
            <Calendar className="h-4 w-4 mr-2" />
            Schedule Demo
          </>
        )}
      </Button>

      <CalendarBooking
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        userEmail={userEmail}
        userName={userName}
        onBookingComplete={onBookingComplete}
        onBookingError={onBookingError}
      />
    </>
  );
};
